import { useState, useRef, useEffect, KeyboardEvent } from 'react';

import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { DocIds } from '@/api/types/backend-dto-types';
import { useDebounce } from '@/new/components/apideck/ApideckFilePickerCustom/utils/useDebounce';
import { Icon } from '@/new/components/icon';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/new/components/shadcn/command';
import { Popover, PopoverContent, PopoverAnchor } from '@/new/components/shadcn/popover';
import { cn } from '@/new/lib/utils';

interface DocMentionsInputProps {
  value: string; // Internal format: @[fileName](docId)
  onChange: (value: string) => void;
  onSubmit?: () => void;
  matterId: string;
  subsetId: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const sanitizeFileName = (fileName: string) => {
  return fileName.replace(/[[\]()]/g, '');
};

export function DocMentionsInput({
  value,
  onChange,
  onSubmit,
  matterId,
  subsetId,
  placeholder = 'Ask Kim...',
  className,
  disabled = false,
}: DocMentionsInputProps) {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const editorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Tracks the last value set by user input, so we can skip innerHTML replacement for self-triggered changes
  const lastInputValueRef = useRef(value);

  // Fetch documents
  const debouncedSearch = useDebounce(mentionSearch, 200);
  const { data: initialDocs } = useGetDocIds(matterId, subsetId, { limit: 1000 });
  const { data: searchDocs, isFetching: isSearching } = useGetDocIds(matterId, subsetId, {
    limit: 1000,
    searchQuery: debouncedSearch || undefined,
  });
  const isLoading = mentionSearch !== debouncedSearch || isSearching;
  const docs = debouncedSearch ? (searchDocs?.documents ?? []) : (initialDocs?.documents ?? []);

  // Reset selected index when docs change
  useEffect(() => {
    setSelectedIndex(0);
  }, [mentionSearch]);

  // Scroll selected item into view
  useEffect(() => {
    if (!showMentions || !listRef.current) return;
    const item = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
    if (item) {
      (item as HTMLElement).scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, showMentions]);

  // Convert internal value format (@[fileName](docId)) to display HTML
  const valueToHTML = (val: string): string => {
    if (!val) return '';

    const escapeHTML = (str: string) => {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    const mentionRegex = /@\[(.*?)\]\((.*?)\)/g;
    let lastIndex = 0;
    let result = '';

    let match;
    while ((match = mentionRegex.exec(val)) !== null) {
      result += escapeHTML(val.slice(lastIndex, match.index));

      const fileName = match[1];
      const docId = match[2];
      result += `<span class="mention group/mention" data-doc-id="${docId}" data-file-name="${escapeHTML(fileName)}" contenteditable="false"><span class="bg-sky-50 py-0.5 -my-0.5 text-blue-600 rounded-lg px-1.5 inline-flex items-center">@${escapeHTML(sanitizeFileName(fileName))}<span class="mention-remove inline-flex items-center justify-center overflow-hidden w-0 h-3.5 ml-0 opacity-0 group-hover/mention:w-3.5 group-hover/mention:ml-1.25 group-hover/mention:opacity-100 rounded-full text-blue-400 hover:text-blue-600 transition-all duration-150 cursor-pointer after:content-['×']" data-mention-remove="true"></span></span></span>`;

      lastIndex = match.index + match[0].length;
    }

    result += escapeHTML(val.slice(lastIndex));
    result = result.replace(/\n/g, '<br>');

    return result;
  };

  // Convert display HTML back to internal format
  const htmlToValue = (html: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = html;

    const mentions = temp.querySelectorAll('.mention');
    mentions.forEach((mention) => {
      const fileName = mention.getAttribute('data-file-name');
      const docId = mention.getAttribute('data-doc-id');
      const textNode = document.createTextNode(`@[${fileName}](${docId})`);
      mention.replaceWith(textNode);
    });

    temp.querySelectorAll('br').forEach((br) => {
      br.replaceWith(document.createTextNode('\n'));
    });

    return temp.textContent || '';
  };

  // Get cursor position in text content (visible text)
  const getCursorPosition = (): number => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current) return 0;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editorRef.current);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    return preCaretRange.toString().length;
  };

  // Convert cursor position from visible text to internal value format
  const mapVisiblePosToValuePos = (visiblePos: number, visibleText: string, internalValue: string): number => {
    const mentionRegex = /@\[(.*?)\]\((.*?)\)/g;
    let valuePos = 0;
    let currentVisiblePos = 0;
    let match;
    let lastIndex = 0;

    while ((match = mentionRegex.exec(internalValue)) !== null) {
      const mentionStart = match.index;
      const mentionEnd = match.index + match[0].length;
      const fileName = match[1];

      const beforeMention = internalValue.slice(lastIndex, mentionStart);
      const beforeLength = beforeMention.length;

      if (currentVisiblePos + beforeLength >= visiblePos) {
        return valuePos + (visiblePos - currentVisiblePos);
      }

      valuePos += beforeLength;
      currentVisiblePos += beforeLength;

      // This mention appears as @sanitizedFileName in visible text
      const visibleMentionLength = sanitizeFileName(fileName).length + 1; // +1 for @

      if (currentVisiblePos + visibleMentionLength >= visiblePos) {
        return mentionEnd;
      }

      valuePos = mentionEnd;
      currentVisiblePos += visibleMentionLength;
      lastIndex = mentionEnd;
    }

    return valuePos + (visiblePos - currentVisiblePos);
  };

  // Set cursor position
  const setCursorPosition = (pos: number) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    const range = document.createRange();

    let currentPos = 0;
    let targetNode: Node | null = null;
    let targetOffset = 0;

    const walker = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT);

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const textLength = node.textContent?.length || 0;

      if (currentPos + textLength >= pos) {
        targetNode = node;
        targetOffset = pos - currentPos;
        break;
      }

      currentPos += textLength;
    }

    if (targetNode) {
      range.setStart(targetNode, targetOffset);
      range.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  // Handle click on mention remove button
  const handleMentionRemove = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const removeBtn = target.hasAttribute('data-mention-remove') ? target : target.closest('[data-mention-remove]');
    if (!removeBtn) return;

    e.preventDefault();
    e.stopPropagation();

    const mentionEl = removeBtn.closest('.mention');
    if (!mentionEl) return;

    const docId = mentionEl.getAttribute('data-doc-id');
    const fileName = mentionEl.getAttribute('data-file-name');
    if (!docId || !fileName) return;

    const escapedFileName = fileName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const escapedDocId = docId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const mentionRegex = new RegExp(`@\\[${escapedFileName}\\]\\(${escapedDocId}\\)\\s?`);

    const mentionMatch = mentionRegex.exec(value);
    if (!mentionMatch) return;

    const currentCursorPos = getCursorPosition();

    // Calculate visible position where the mention starts
    const beforeMention = value.slice(0, mentionMatch.index);
    const innerRegex = /@\[(.*?)\]\((.*?)\)/g;
    let visibleMentionStart = 0;
    let lastIdx = 0;
    let m;
    while ((m = innerRegex.exec(beforeMention)) !== null) {
      visibleMentionStart += m.index - lastIdx;
      visibleMentionStart += sanitizeFileName(m[1]).length + 1;
      lastIdx = m.index + m[0].length;
    }
    visibleMentionStart += beforeMention.length - lastIdx;

    // Calculate visible length of what's being removed
    const visibleMentionLen = sanitizeFileName(fileName).length + 1;
    const hasTrailingSpace = mentionMatch[0].length > `@[${fileName}](${docId})`.length;
    const totalVisibleRemoved = visibleMentionLen + (hasTrailingSpace ? 1 : 0);

    // Adjust cursor: shift back if it was after the mention, otherwise keep it
    const newCursorPos =
      currentCursorPos <= visibleMentionStart ? currentCursorPos : currentCursorPos - totalVisibleRemoved;

    const newValue = value.replace(mentionRegex, '');
    onChange(newValue);

    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.focus();
        setCursorPosition(Math.max(0, newCursorPos));
      }
    }, 0);
  };

  // Handle input changes
  const handleInput = () => {
    if (!editorRef.current) return;

    const html = editorRef.current.innerHTML;
    const textValue = htmlToValue(html);
    const visibleCursorPos = getCursorPosition();
    const visibleText = editorRef.current.textContent || '';

    lastInputValueRef.current = textValue;
    onChange(textValue);

    // Check for @ trigger
    const textBeforeCursor = textValue.slice(0, textValue.length) || '';
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';

      const afterAt = textBeforeCursor.slice(lastAtIndex);
      const isMentionFormat = /^@\[.*?\]\(.*?\)/.test(afterAt);

      const isWhitespaceBefore = charBeforeAt === ' ' || charBeforeAt === '\u00A0' || charBeforeAt === '\n';

      if (!isMentionFormat && (isWhitespaceBefore || lastAtIndex === 0)) {
        const searchText = textBeforeCursor.slice(lastAtIndex + 1);
        if (!searchText.includes('\n') && !/[\s\u00A0]{2}/.test(searchText)) {
          const valueAtPos = mapVisiblePosToValuePos(
            visibleText.lastIndexOf('@', visibleCursorPos),
            visibleText,
            textValue,
          );
          setMentionSearch(searchText);
          setMentionStartIndex(valueAtPos);
          setShowMentions(true);
          return;
        }
      }
    }

    setShowMentions(false);
  };

  // Insert mention
  const insertMention = (doc: DocIds) => {
    if (!editorRef.current || mentionStartIndex === null) return;

    setShowMentions(false);
    setMentionStartIndex(null);

    setTimeout(() => {
      setMentionSearch('');
    }, 150);

    const visibleCursorPos = getCursorPosition();
    const visibleText = editorRef.current.textContent || '';

    const valueCursorPos = mapVisiblePosToValuePos(visibleCursorPos, visibleText, value);

    const before = value.slice(0, mentionStartIndex);
    const after = value.slice(valueCursorPos);
    const fileName = doc.file.name;
    const newValue = `${before}@[${fileName}](${doc.id})\u00A0${after}`;

    onChange(newValue);

    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = valueToHTML(newValue);

        let visibleBeforeLength = 0;
        const mentionRegex = /@\[(.*?)\]\((.*?)\)/g;
        let lastIndex = 0;
        let match;

        while ((match = mentionRegex.exec(before)) !== null) {
          visibleBeforeLength += match.index - lastIndex;
          visibleBeforeLength += sanitizeFileName(match[1]).length + 1; // +1 for @
          lastIndex = match.index + match[0].length;
        }
        visibleBeforeLength += before.length - lastIndex;

        const newVisibleCursorPos = visibleBeforeLength + sanitizeFileName(fileName).length + 2; // +2 for @ and space
        setCursorPosition(newVisibleCursorPos);
        editorRef.current.focus();
      }
    }, 0);
  };

  // Strip rich text on paste — only allow plain text
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  // Handle keyboard events
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (showMentions) {
      if (docs.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % docs.length);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + docs.length) % docs.length);
        } else if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          insertMention(docs[selectedIndex]);
        } else if (e.key === 'Escape') {
          setShowMentions(false);
        }
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
      } else if (e.key === 'Escape') {
        setShowMentions(false);
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit?.();
    } else if (e.key === 'Enter' && e.shiftKey) {
      // Allow default behavior (newline via contentEditable)
    } else if (e.key === 'Backspace') {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (range.collapsed && range.startOffset === 0) {
          const previousNode = range.startContainer.previousSibling;

          if (previousNode && (previousNode as Element).classList?.contains('mention')) {
            e.preventDefault();
            previousNode.remove();
            handleInput();
          }
        }
      }
    }
  };

  const isEmpty = !value || value.trim() === '';

  // Update display when value changes externally (e.g. mention removal).
  // Skips innerHTML replacement for user-typed input to preserve DOM state (hover, transitions).
  useEffect(() => {
    if (editorRef.current && value !== lastInputValueRef.current) {
      lastInputValueRef.current = value;
      const cursorPos = getCursorPosition();
      editorRef.current.innerHTML = valueToHTML(value);

      if (document.activeElement === editorRef.current) {
        setCursorPosition(cursorPos);
      }
    }
  }, [value]);

  return (
    <Popover open={showMentions}>
      <PopoverAnchor asChild className="relative min-h-32 bg-white">
        <div
          ref={editorRef}
          contentEditable={!disabled}
          onBlur={() => setShowMentions(false)}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onMouseDown={handleMentionRemove}
          onPaste={handlePaste}
          data-placeholder={placeholder}
          suppressContentEditableWarning
          className={cn(
            'w-full overflow-y-auto rounded-md border bg-white px-4 py-3 text-sm ring-offset-2 ring-offset-transparent transition-colors duration-150 before:absolute focus-visible:ring-1 focus-visible:ring-teal-500 focus-visible:ring-offset-white focus-visible:outline-none',
            isEmpty && 'before:text-gray-400/80 before:content-[attr(data-placeholder)]',
            disabled && 'opacity-50',
            className,
          )}
        />
      </PopoverAnchor>

      <PopoverContent
        className="mb-1 w-[var(--radix-popover-trigger-width)] max-w-none p-0 shadow-lg/2"
        align="start"
        side="top"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command>
          <CommandList ref={listRef}>
            <CommandEmpty className="pt-8">
              {isLoading ? (
                <span className="flex items-center justify-center gap-2 text-gray-400">
                  <Icon className="lucide--loader h-4 w-4 animate-spin" />
                  Searching documents...
                </span>
              ) : (
                <>No documents found matching &ldquo;{mentionSearch}&rdquo;</>
              )}
            </CommandEmpty>
            <CommandGroup>
              {docs.map((doc, index) => (
                <CommandItem
                  key={doc.id}
                  value={doc.file.name}
                  data-index={index}
                  onSelect={() => insertMention(doc)}
                  className={cn('cursor-pointer text-gray-600', index === selectedIndex && 'bg-gray-50 text-gray-900')}
                >
                  <span className="truncate">{sanitizeFileName(doc.file.name)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
